import React, { useEffect, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaImage,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import { uploadImage } from "../../../../../services/eventsServices";

import {
  emptyEventForm,
  mapEventToForm,
  resolveMediaUrl,
} from "../utils/agendaUtils";

export default function EventFormDrawer({
  isOpen,
  mode = "create",
  event,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyEventForm);
  const [imageFile, setImageFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && event) {
      setFormData(mapEventToForm(event));
    } else {
      setFormData(emptyEventForm);
    }

    setImageFile(null);
    setLocalPreview("");
    setUploading(false);
  }, [isOpen, isEdit, event]);

  useEffect(() => {
    if (!imageFile) return undefined;

    const url = URL.createObjectURL(imageFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const title = isEdit ? "Modifier un événement" : "Créer un événement";

  const subtitle = isEdit
    ? "Mettez à jour le contenu, la date, le lieu, l’image et la visibilité."
    : "Ajoutez un nouvel événement à l’agenda de l’association sélectionnée.";

  const preview =
    localPreview || resolveMediaUrl(formData.photoURL, "/images/event_placeholder.jpg");

  const handleChange = (changeEvent) => {
    const { name, value, type, checked } = changeEvent.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const uploadSingleImage = async () => {
    if (!imageFile) return formData.photoURL;

    const imageData = new FormData();
    imageData.append("image", imageFile);
    imageData.append("category", "events");

    const response = await uploadImage(imageData);

    return (
      response?.filePath ||
      response?.url ||
      response?.path ||
      response?.photoURL ||
      response?.data?.filePath ||
      response?.data?.url ||
      ""
    );
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    if (!formData.nom_event.trim()) {
      toast.error("Le nom de l'événement est obligatoire.");
      return;
    }

    if (!formData.date_event) {
      toast.error("La date de l'événement est obligatoire.");
      return;
    }

    try {
      setUploading(true);

      const uploadedUrl = await uploadSingleImage();

      const nextFormData = {
        ...formData,
        photoURL: uploadedUrl,
      };

      if (isEdit) {
        await onUpdate?.(event.id, nextFormData, event);
      } else {
        await onCreate?.(nextFormData);
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'événement :", error);
      toast.error("Erreur lors de l'enregistrement de l'événement.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaCalendarAlt />}
            title="Informations de l’événement"
            subtitle="Nom, description et visibilité de l’événement."
          >
            <div className="grid gap-4">
              <InputField
                label="Nom de l'événement"
                name="nom_event"
                value={formData.nom_event}
                onChange={handleChange}
                required
                placeholder="Ex : Stage, forum, compétition..."
              />

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Description
                </label>

                <textarea
                  name="description_event"
                  value={formData.description_event}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Description de l'événement..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(formData.is_active)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />

                <span className="inline-flex items-center gap-2">
                  <FaCheckCircle
                    className={
                      formData.is_active ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  Événement actif et visible
                </span>
              </label>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaMapMarkerAlt />}
            title="Date & lieu"
            subtitle="Ces informations seront visibles sur la page publique."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Date de l'événement"
                name="date_event"
                type="date"
                value={formData.date_event}
                onChange={handleChange}
                required
              />

              <InputField
                label="Lieu"
                name="lieu_event"
                value={formData.lieu_event}
                onChange={handleChange}
                placeholder="Ex : Salle municipale, dojo..."
              />
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaImage />}
            title="Image de l’événement"
            subtitle="Ajoutez une URL ou téléversez une nouvelle image."
          >
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="space-y-4">
                <InputField
                  label="URL de l'image"
                  name="photoURL"
                  value={formData.photoURL}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                />

                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                  <FaCloudUploadAlt />
                  {imageFile ? imageFile.name : "Choisir un fichier"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setImageFile(e.target.files?.[0] || null)
                    }
                    className="hidden"
                  />
                </label>

                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Si une image est téléversée, elle remplace l’URL saisie pour
                  cet enregistrement.
                </p>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
                  Aperçu
                </p>

                <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-900">
                  <img
                    src={preview}
                    alt="Aperçu événement"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "/images/event_placeholder.jpg";
                    }}
                  />
                </div>
              </div>
            </div>
          </FormBlock>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving || uploading
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">
            {title}
          </h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}